import React from 'react';
import { Link } from 'react-router-dom';
import { GOLD, GOLD_LT, MAPS_URL } from '../theme';
import useCategories from '../data/useCategories';
import { WHATSAPP_NUMBER } from '../data/whatsapp';

const INSTAGRAM_URL = 'https://www.instagram.com/ijcouture77/';
const WHATSAPP_URL = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}`;
// Displayed in the international form so it is dialable from outside Pakistan.
const PHONE_DISPLAY = '+92 343 975 6320';

function InstagramIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Every entry now resolves somewhere. The previous version rendered any item
// without a matching href as a plain <span> — so the whole Explore column
// (Bridal, Pret, Formal, Party Wear) looked like navigation and did nothing,
// and Shipping/Contact/Made to Measure were dead alongside it.
// Hardcoding these meant the footer said "Party Wear" while the category
// itself was published as "Party-wear", and a category added in the admin
// never appeared here at all. The catalogue is the source of truth for both
// the label and the set.
const FALLBACK_EXPLORE = [
  ['Bridal', { to: '/categories/bridal' }],
  ['Pret', { to: '/categories/pret' }],
  ['Formal', { to: '/categories/formal' }],
  ['Party Wear', { to: '/categories/party-wear' }],
];

const STATIC_COLUMNS = [
  ['Client Care', [
    ['Book Appointment', { to: '/book-appointment' }],
    ['Made to Measure', { to: '/about' }],
    ['WhatsApp Us', { href: WHATSAPP_URL, external: true }],
    [PHONE_DISPLAY, { href: `tel:+${WHATSAPP_NUMBER}` }],
  ]],
  ['Visit', [
    ['About', { to: '/about' }],
    ['F-7 Markaz', { href: MAPS_URL, external: true }],
    ['Islamabad, Pakistan', { href: MAPS_URL, external: true }],
    ['@ijcouture', { href: INSTAGRAM_URL, external: true, icon: 'instagram' }],
  ]],
];

const itemStyle = { display: 'flex', alignItems: 'center', gap: 8, color: 'inherit', textDecoration: 'none' };

function FooterItem({ label, cfg }) {
  const body = (
    <>
      {cfg.icon === 'instagram' && <InstagramIcon />}
      {label}
    </>
  );
  if (cfg.to) return <Link to={cfg.to} style={itemStyle}>{body}</Link>;
  return (
    <a
      href={cfg.href}
      {...(cfg.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      style={itemStyle}
    >
      {body}
    </a>
  );
}

export default function Footer() {
  const { categories } = useCategories();
  const explore = categories.length > 0
    ? categories.map((c) => [c.name, { to: `/categories/${c.slug}` }])
    : FALLBACK_EXPLORE;
  const COLUMNS = [['Explore', explore], ...STATIC_COLUMNS];

  return (
    <footer id="visit" style={{ background: '#100c08', color: '#efe4d2', padding: 'clamp(54px,8vh,100px) clamp(20px,4vw,60px) 34px', borderTop: '1px solid rgba(201,162,75,.2)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 44, justifyContent: 'space-between', paddingBottom: 50, borderBottom: '1px solid rgba(201,162,75,.18)' }}>
        <div style={{ maxWidth: 440 }}>
          <div style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 700, fontSize: 'clamp(28px,4vw,52px)', lineHeight: .88, color: GOLD_LT }}>IJ<br />COUTURE</div>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'rgba(239,228,210,.7)', marginTop: 22 }}>Handcrafted bridal couture in Islamabad. <Link to="/about" style={{ color: GOLD_LT, textDecoration: 'none' }}>Read our story →</Link></p>
          <p style={{ fontSize: 15, lineHeight: 1.8, color: 'rgba(239,228,210,.55)', marginTop: 18 }}>
            Atelier visits by appointment.<br />
            Made-to-measure orders take 3–4 weeks.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'clamp(34px,5vw,84px)', flexWrap: 'wrap' }}>
          {COLUMNS.map(([title, items]) => (
            <nav key={title} aria-label={title}>
              <div style={{ fontWeight: 600, fontSize: 13, letterSpacing: '.28em', textTransform: 'uppercase', color: GOLD, marginBottom: 18 }}>{title}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 16, color: 'rgba(239,228,210,.82)' }}>
                {items.map(([label, cfg]) => <FooterItem key={label} label={label} cfg={cfg} />)}
              </div>
            </nav>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, paddingTop: 24, fontSize: 13, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(239,228,210,.5)' }}>
        <span>© {new Date().getFullYear()} IJ Couture</span><span>Made in Islamabad</span>
      </div>
    </footer>
  );
}
